import * as React from "react";
import { Message, Button, Icon, Transition } from "semantic-ui-react";
import "./AnnouncementBar.css";

export interface AnnouncementBarProps {
  /** Unique identifier for this announcement (used for localStorage) */
  id: string;
  /** The announcement message to display */
  message: string;
  /** Optional link text for the call-to-action button */
  linkText?: string;
  /** Optional link URL for the call-to-action button */
  linkHref?: string;
  /** Background color or gradient (default: orange) */
  color?: string;
  /** Text color for accessibility (default: white) */
  textColor?: string;
  /** Whether the banner can be dismissed (default: true) */
  dismissible?: boolean;
  /** Position of the banner (default: fixed) */
  position?: "fixed" | "static" | "sticky";
  /** Z-index for the banner (default: 1300) */
  zIndex?: number;
  /** Animation duration in milliseconds (default: 500) */
  animationDuration?: number;
  /** Optional callback when banner is dismissed */
  onDismiss?: () => void;
}

interface AnnouncementBarState {
  visible: boolean;
}

class AnnouncementBar extends React.Component<AnnouncementBarProps, AnnouncementBarState> {
  static defaultProps = {
    color: "linear-gradient(90deg, #ff4081, #ff9800)",
    textColor: "white",
    dismissible: true,
    position: "fixed",
    zIndex: 1300,
    animationDuration: 500,
  };

  constructor(props: AnnouncementBarProps) {
    super(props);

    // Check localStorage to see if this announcement has been dismissed
    const isDismissed = this.getLocalStorageState();
    
    this.state = {
      visible: !isDismissed,
    };
  }

  /**
   * Gets the localStorage key for this announcement
   */
  private getStorageKey(): string {
    return `announcement_dismissed_${this.props.id}`;
  }

  /**
   * Checks if the announcement has been dismissed in localStorage
   */
  private getLocalStorageState(): boolean {
    try {
      const dismissed = localStorage.getItem(this.getStorageKey());
      return dismissed === "true";
    } catch (error) {
      console.warn("Failed to read from localStorage:", error);
      return false;
    }
  }

  /**
   * Saves the dismissed state to localStorage
   */
  private setLocalStorageState(dismissed: boolean): void {
    try {
      localStorage.setItem(this.getStorageKey(), dismissed.toString());
    } catch (error) {
      console.warn("Failed to write to localStorage:", error);
    }
  }

  /**
   * Handles dismissing the banner
   */
  private handleDismiss = (): void => {
    this.setState({ visible: false });
    this.setLocalStorageState(true);
    
    if (this.props.onDismiss) {
      this.props.onDismiss();
    }
  };

  /**
   * Public method to clear the dismissed state (useful for testing or resetting)
   */
  public clearDismissedState = (): void => {
    this.setLocalStorageState(false);
    this.setState({ visible: true });
  };

  render() {
    const {
      message,
      linkText,
      linkHref,
      color,
      textColor,
      dismissible,
      position,
      zIndex,
      animationDuration,
    } = this.props;

    const { visible } = this.state;

    // Don't render anything if not visible (after animation completes)
    if (!visible && animationDuration === 0) {
      return null;
    }

    return (
      <Transition
        visible={visible}
        animation="slide down"
        duration={animationDuration}
        unmountOnHide
      >
        <Message
          className="announcement-bar"
          style={{
            position,
            top: 0,
            left: 0,
            width: "100%",
            background: color,
            color: textColor,
            textAlign: "center",
            margin: 0,
            borderRadius: 0,
            zIndex,
            fontWeight: 500,
            border: "none",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          <div className="announcement-bar-content">
            <span className="announcement-bar-message">{message}</span>
            {linkHref && (
              <Button
                basic
                inverted
                color="black"
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer"
                size="mini"
                className="announcement-bar-button"
              >
                {linkText || "Learn more"}
              </Button>
            )}
            {dismissible && (
              <Icon
                name="close"
                onClick={this.handleDismiss}
                className="announcement-bar-close"
                aria-label="Dismiss announcement"
                role="button"
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    this.handleDismiss();
                  }
                }}
              />
            )}
          </div>
        </Message>
      </Transition>
    );
  }
}

export default AnnouncementBar;

